import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getTransactions, createTransaction, analyzeTransaction, analyzeTransactionsBatch, uploadTransactions, deleteTransaction, getClients } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DateRangePicker from '../components/DateRangePicker';
import {
  Plus,
  Upload,
  Brain,
  Eye,
  Trash,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  X
} from '@phosphor-icons/react';

function ConfidenceBadge({ score }) {
  if (!score && score !== 0) return <span className="text-xs text-zinc-400">—</span>;
  
  if (score >= 80) {
    return (
      <span className="px-2 py-1 text-xs font-mono bg-emerald-50 text-emerald-700 border border-emerald-500 rounded-sm" data-testid="confidence-badge-high">
        {score}%
      </span>
    );
  } else if (score >= 50) {
    return (
      <span className="px-2 py-1 text-xs font-mono bg-amber-50 text-amber-700 border border-amber-500 rounded-sm" data-testid="confidence-badge-medium">
        {score}%
      </span>
    );
  }
  return (
    <span className="px-2 py-1 text-xs font-mono bg-red-50 text-red-700 border border-red-500 rounded-sm" data-testid="confidence-badge-low">
      {score}%
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-zinc-100 text-zinc-600 border-zinc-300',
    pending_review: 'bg-amber-50 text-amber-700 border-amber-500',
    classified: 'bg-blue-50 text-blue-700 border-blue-500',
    posted: 'bg-emerald-50 text-emerald-700 border-emerald-500',
    rejected: 'bg-red-50 text-red-700 border-red-500'
  };
  
  return (
    <span className={`px-2 py-1 text-xs uppercase tracking-wider border rounded-sm ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {status?.replace('_', ' ') || 'pending'}
    </span>
  );
}

function TransactionDetailModal({ transaction, onClose }) {
  if (!transaction) return null;
  
  const analysis = transaction.ai_analysis || {};
  const patternEvidence = analysis.pattern_evidence || {};
  const contextEvidence = analysis.context_evidence || {};
  const hasHybridAnalysis = !!analysis.pattern_evidence;
  
  return (
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="transaction-detail-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between">
          <span>Transaction Details</span>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={20} />
          </button>
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-6 py-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Vendor</p>
            <p className="font-medium">{transaction.vendor_name}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Date</p>
            <p className="font-mono">{transaction.date}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Amount</p>
            <p className="font-mono text-lg">${Math.abs(transaction.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Status</p>
            <StatusBadge status={transaction.status} />
          </div>
        </div>
        
        <div>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Description</p>
          <p className="text-sm text-zinc-600">{transaction.description}</p>
        </div>
        
        {transaction.memo && (
          <div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Memo</p>
            <p className="text-sm text-zinc-600">{transaction.memo}</p>
          </div>
        )}
        
        {/* HYBRID DUAL-EVIDENCE ANALYSIS */}
        {hasHybridAnalysis && (
          <div className="border-t border-zinc-200 pt-6 space-y-4">
            {/* 3-Line Trust Display - Prominent */}
            {analysis.pattern_summary && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="trust-display-detail">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-violet-100 border border-violet-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-violet-500 rounded-full"></span>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-violet-600 mb-0.5">Suggested by Pattern</p>
                    <p className="text-sm text-zinc-800">{analysis.pattern_summary}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-100 border border-blue-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-blue-600 mb-0.5">Checked Against Company Policy</p>
                    <p className="text-sm text-zinc-800">{analysis.policy_check}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    analysis.decision === 'auto_post' ? 'bg-emerald-100 border border-emerald-300' :
                    analysis.requires_review ? 'bg-amber-100 border border-amber-300' : 'bg-zinc-100 border border-zinc-300'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      analysis.decision === 'auto_post' ? 'bg-emerald-500' :
                      analysis.requires_review ? 'bg-amber-500' : 'bg-zinc-500'
                    }`}></span>
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-widest mb-0.5 ${
                      analysis.decision === 'auto_post' ? 'text-emerald-600' :
                      analysis.requires_review ? 'text-amber-600' : 'text-zinc-500'
                    }`}>
                      {analysis.requires_review ? 'Why Review Is Needed' : analysis.decision === 'auto_post' ? 'Auto-Posted' : 'Decision'}
                    </p>
                    <p className="text-sm text-zinc-800">{analysis.review_reason_display}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Decision Summary */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain size={20} weight="duotone" className="text-violet-600" />
                <h3 className="font-semibold">Hybrid AI Analysis</h3>
              </div>
              <div className="flex items-center gap-3">
                <ConfidenceBadge score={analysis.confidence_score} />
                {analysis.decision && (
                  <span className={`px-2 py-1 text-xs uppercase tracking-wider border rounded-sm ${
                    analysis.decision === 'auto_post' ? 'bg-emerald-50 text-emerald-700 border-emerald-500' :
                    analysis.decision === 'review' ? 'bg-amber-50 text-amber-700 border-amber-500' :
                    'bg-blue-50 text-blue-700 border-blue-500'
                  }`}>
                    {analysis.decision.replace('_', ' ')}
                  </span>
                )}
              </div>
            </div>

            {analysis.suggested_account && (
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Suggested:</span>
                <span className="font-mono font-medium">{analysis.suggested_account}</span>
                {analysis.context_overrides_pattern && (
                  <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">Context Override</span>
                )}
              </div>
            )}

            {/* Dual Evidence Display */}
            <div className="grid grid-cols-2 gap-4">
              {/* Pattern Evidence */}
              <div className="bg-violet-50 border border-violet-200 p-4 rounded-sm" data-testid="pattern-evidence-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Brain size={16} weight="duotone" className="text-violet-600" />
                  <p className="text-xs uppercase tracking-widest text-violet-600">Suggested by Pattern</p>
                </div>
                <div className="space-y-2 text-sm">
                  {patternEvidence.vendor_match && (
                    <p className="text-violet-900">Vendor: <span className="font-medium">{patternEvidence.vendor_match}</span></p>
                  )}
                  {patternEvidence.recurrence && patternEvidence.recurrence !== 'unknown' && (
                    <p className="text-violet-900">Recurrence: <span className="font-medium capitalize">{patternEvidence.recurrence}</span></p>
                  )}
                  {patternEvidence.similar_prior_transactions > 0 && (
                    <p className="text-violet-900">Similar prior: <span className="font-mono font-medium">{patternEvidence.similar_prior_transactions}</span></p>
                  )}
                  {patternEvidence.confidence !== undefined && (
                    <p className="text-violet-900">Pattern confidence: <span className="font-mono font-medium">{patternEvidence.confidence}%</span></p>
                  )}
                  {patternEvidence.suggested_account && (
                    <p className="text-violet-900">Suggested: <span className="font-mono font-medium">{patternEvidence.suggested_account}</span></p>
                  )}
                  {patternEvidence.signals?.length > 0 && (
                    <div className="pt-2 border-t border-violet-200 mt-2">
                      <p className="text-xs text-violet-500 mb-1">Signals:</p>
                      {patternEvidence.signals.map((s) => (
                        <p key={`signal-${s}`} className="text-xs text-violet-800">- {s}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Context Evidence */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm" data-testid="context-evidence-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Eye size={16} weight="duotone" className="text-blue-600" />
                  <p className="text-xs uppercase tracking-widest text-blue-600">Checked Against Policy</p>
                </div>
                <div className="space-y-2 text-sm">
                  {contextEvidence.industry && (
                    <p className="text-blue-900">Industry: <span className="font-medium capitalize">{contextEvidence.industry.replace(/_/g, ' ')}</span></p>
                  )}
                  {contextEvidence.policy_match && (
                    <p className="text-blue-900">Policy: <span className="font-medium">{contextEvidence.policy_match}</span></p>
                  )}
                  {contextEvidence.materiality_check && (
                    <p className="text-blue-900">Materiality: <span className="font-medium">{contextEvidence.materiality_check}</span></p>
                  )}
                  {contextEvidence.automation_level && (
                    <p className="text-blue-900">Automation: <span className="font-medium capitalize">{contextEvidence.automation_level}</span></p>
                  )}
                  {contextEvidence.rules_applied?.length > 0 && (
                    <div className="pt-2 border-t border-blue-200 mt-2">
                      <p className="text-xs text-blue-500 mb-1">Rules applied:</p>
                      {contextEvidence.rules_applied.map((r) => (
                        <p key={`rule-${r}`} className="text-xs text-blue-800">- {r.replace(/_/g, ' ')}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Review Reasons */}
            {analysis.requires_review && analysis.review_reason && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm" data-testid="review-reason-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Eye size={16} weight="duotone" className="text-amber-600" />
                  <p className="text-xs uppercase tracking-widest text-amber-600">Why Review Is Needed</p>
                </div>
                <p className="text-sm text-amber-900">{analysis.review_reason}</p>
              </div>
            )}

            {/* Explanation */}
            {analysis.explanation && (
              <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Plain-English Explanation</p>
                <p className="text-sm text-zinc-700 leading-relaxed">{analysis.explanation}</p>
              </div>
            )}
          </div>
        )}

        {/* Legacy analysis fallback */}
        {analysis && !hasHybridAnalysis && !analysis.error && analysis.confidence_score !== undefined && (
          <div className="border-t border-zinc-200 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Brain size={20} weight="duotone" className="text-violet-600" />
              <h3 className="font-semibold">AI Analysis</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-sm text-zinc-600">Confidence:</span>
                <ConfidenceBadge score={analysis.confidence_score} />
              </div>
              {analysis.suggested_account && (
                <p className="text-sm"><span className="text-zinc-500">Suggested:</span> <span className="font-mono">{analysis.suggested_account}</span></p>
              )}
              {analysis.explanation && (
                <p className="text-sm text-zinc-600">{analysis.explanation}</p>
              )}
            </div>
          </div>
        )}
        
        {analysis?.error && (
          <div className="bg-zinc-100 border border-zinc-200 p-4 rounded-sm">
            <p className="text-sm text-zinc-600">AI analysis not available: {analysis.error}</p>
          </div>
        )}
      </div>
    </DialogContent>
  );
}

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [clients, setClients] = useState([]);
  
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor_name: '',
    description: '',
    amount: '',
    memo: ''
  });

  const limit = 20;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        skip: page * limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo })
      };
      const response = await getTransactions(params);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    getClients().then(res => setClients(res.data.clients || [])).catch(() => {});
  }, []);

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await createTransaction({
        ...newTransaction,
        amount: parseFloat(newTransaction.amount)
      });
      toast.success('Transaction added');
      setIsAddDialogOpen(false);
      setNewTransaction({
        date: new Date().toISOString().split('T')[0],
        vendor_name: '',
        description: '',
        amount: '',
        memo: ''
      });
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to add transaction');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const response = await uploadTransactions(file);
      toast.success(`Imported ${response.data.count} transactions`);
      setIsUploadDialogOpen(false);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleAnalyze = async (transactionId) => {
    setAnalyzing(true);
    try {
      await analyzeTransaction(transactionId);
      toast.success('Transaction analyzed');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to analyze transaction');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleBatchAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await analyzeTransactionsBatch();
      toast.success(response.data.message);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to analyze transactions');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await deleteTransaction(transactionId);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const filteredTransactions = transactions.filter(txn => 
    searchTerm === '' || 
    txn.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    txn.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout title="Transactions">
      <div data-testid="transactions-page" className="space-y-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              type="text"
              placeholder="Search vendor or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 rounded-sm border-zinc-200"
              data-testid="transaction-search-input"
            />
          </div>
          
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={({ dateFrom: df, dateTo: dt }) => { setDateFrom(df); setDateTo(dt); setPage(0); }}
          />
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-10 rounded-sm" data-testid="status-filter-select">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="classified">Classified</SelectItem>
              <SelectItem value="pending_review">Needs Review</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={handleBatchAnalyze}
            disabled={analyzing}
            className="h-10 rounded-sm border-zinc-200"
            data-testid="batch-analyze-button"
          >
            <Brain size={18} className="mr-2" />
            {analyzing ? 'Analyzing...' : 'Analyze All'}
          </Button>
          
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-10 rounded-sm border-zinc-200" data-testid="upload-csv-button">
                <Upload size={18} className="mr-2" />
                Upload CSV
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle>Upload Transactions</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-zinc-600 mb-4">
                  Upload a CSV file with columns: date, vendor, description, amount, memo
                </p>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                  data-testid="file-upload-input"
                />
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="add-transaction-button">
                <Plus size={18} className="mr-2" />
                Add Transaction
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-transaction-dialog">
              <DialogHeader>
                <DialogTitle>Add Transaction</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddTransaction} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
                    <Input
                      type="date"
                      value={newTransaction.date}
                      onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                      required
                      className="rounded-sm"
                      data-testid="new-txn-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newTransaction.amount}
                      onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                      required
                      placeholder="0.00"
                      className="rounded-sm font-mono"
                      data-testid="new-txn-amount"
                    />
                  </div>
                </div>
                
                {clients.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Client (optional)</Label>
                    <Select
                      value={newTransaction.client_id || ''}
                      onValueChange={(v) => setNewTransaction({ ...newTransaction, client_id: v })}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="new-txn-client">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Vendor Name</Label>
                  <Input
                    type="text"
                    value={newTransaction.vendor_name}
                    onChange={(e) => setNewTransaction({ ...newTransaction, vendor_name: e.target.value })}
                    required
                    placeholder="e.g., Amazon Web Services"
                    className="rounded-sm"
                    data-testid="new-txn-vendor"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
                  <Input
                    type="text"
                    value={newTransaction.description}
                    onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                    required
                    placeholder="e.g., Monthly cloud hosting"
                    className="rounded-sm"
                    data-testid="new-txn-description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Memo (optional)</Label>
                  <Input
                    type="text"
                    value={newTransaction.memo}
                    onChange={(e) => setNewTransaction({ ...newTransaction, memo: e.target.value })}
                    placeholder="Additional notes..."
                    className="rounded-sm"
                    data-testid="new-txn-memo"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="submit-new-txn">
                    Add Transaction
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredTransactions.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full table-dense">
                  <thead>
                    <tr className="text-left">
                      <th className="px-6 pt-4">Date</th>
                      <th className="px-6 pt-4">Vendor</th>
                      <th className="px-6 pt-4 text-right">Amount</th>
                      <th className="px-6 pt-4">Confidence</th>
                      <th className="px-6 pt-4">AI Insight</th>
                      <th className="px-6 pt-4">Status</th>
                      <th className="px-6 pt-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((txn) => {
                      const a = txn.ai_analysis || {};
                      const hasInsight = !!a.pattern_summary;
                      return (
                      <tr key={txn.transaction_id} className="hover:bg-zinc-50 transition-colors duration-150 align-top">
                        <td className="px-6 font-mono text-sm">{txn.date}</td>
                        <td className="px-6">
                          <p className="text-sm font-medium">{txn.vendor_name}</p>
                          <p className="text-xs text-zinc-500 truncate max-w-[200px]">{txn.description}</p>
                        </td>
                        <td className="px-6 text-right font-mono text-sm">
                          ${Math.abs(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6">
                          <ConfidenceBadge score={a.confidence_score} />
                        </td>
                        <td className="px-6 max-w-xs">
                          {hasInsight ? (
                            <div className="space-y-1" data-testid={`trust-display-${txn.transaction_id}`}>
                              <p className="text-xs leading-tight">
                                <span className="inline-block w-1.5 h-1.5 bg-violet-500 rounded-full mr-1.5 align-middle"></span>
                                <span className="text-violet-700">{a.pattern_summary}</span>
                              </p>
                              <p className="text-xs leading-tight">
                                <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mr-1.5 align-middle"></span>
                                <span className="text-blue-700">{a.policy_check}</span>
                              </p>
                              {a.review_reason_display && (
                                <p className="text-xs leading-tight">
                                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${
                                    a.decision === 'auto_post' ? 'bg-emerald-500' :
                                    a.requires_review ? 'bg-amber-500' : 'bg-zinc-400'
                                  }`}></span>
                                  <span className={
                                    a.decision === 'auto_post' ? 'text-emerald-700' :
                                    a.requires_review ? 'text-amber-700' : 'text-zinc-600'
                                  }>{a.review_reason_display}</span>
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-400">Not analyzed</span>
                          )}
                        </td>
                        <td className="px-6">
                          <StatusBadge status={txn.status} />
                        </td>
                        <td className="px-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedTransaction(txn)}
                              className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors"
                              title="View details"
                              data-testid={`view-txn-${txn.transaction_id}`}
                            >
                              <Eye size={16} />
                            </button>
                            {!txn.ai_analysis && (
                              <button
                                onClick={() => handleAnalyze(txn.transaction_id)}
                                disabled={analyzing}
                                className="p-1.5 text-violet-400 hover:text-violet-600 hover:bg-violet-50 rounded-sm transition-colors"
                                title="Analyze with AI"
                                data-testid={`analyze-txn-${txn.transaction_id}`}
                              >
                                <Brain size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(txn.transaction_id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                              title="Delete"
                              data-testid={`delete-txn-${txn.transaction_id}`}
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-between">
                <p className="text-sm text-zinc-500">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded-sm"
                    data-testid="prev-page-button"
                  >
                    <CaretLeft size={16} />
                  </Button>
                  <span className="text-sm text-zinc-600 px-2">
                    Page {page + 1} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                    className="rounded-sm"
                    data-testid="next-page-button"
                  >
                    <CaretRight size={16} />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="px-6 py-12 text-center text-zinc-500">
              <p className="text-sm">No transactions found</p>
            </div>
          )}
        </div>
        
        {/* Detail Modal */}
        <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
          <TransactionDetailModal transaction={selectedTransaction} onClose={() => setSelectedTransaction(null)} />
        </Dialog>
      </div>
    </Layout>
  );
}
